import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Activity, FileText, Send, Radio, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import { TEAM_AVATARS } from '../components/teamAvatars';
import { useOSPulse, eventLabel, timeAgo } from '../hooks/useOSPulse';
import AutoDashboardShowcase from '../components/AutoDashboardShowcase';
import { useT } from '../i18n';

/* /os — KITXENS OS Live. A public window into the autonomous AI Operating
   System: real counters, real event stream, the team working right now.
   Proof, not promises. */

const AGENT_TAGLINE = (es) => ({
  penny: es ? 'Descubriendo temas y publicando conocimiento' : 'Discovering topics & publishing knowledge',
  sonny: es ? 'Adaptando cada artículo a cada red' : 'Adapting every article to every network',
  stan: es ? 'Educando prospectos con contenido' : 'Educating prospects with content',
  rachel: es ? 'Atendiendo llamadas y conversaciones' : 'Handling calls & conversations',
  eva: es ? 'Supervisando el rendimiento del sistema' : 'Supervising system performance',
  linda: es ? 'Vigilando cumplimiento y riesgo' : 'Watching compliance & risk',
});

function Counter({ value, label }) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 text-center">
      <p className="text-3xl sm:text-4xl font-bold text-[#F77F11] tracking-[-0.02em]">{value?.toLocaleString?.() ?? value}</p>
      <p className="mt-1.5 text-[12px] text-neutral-400">{label}</p>
    </div>
  );
}

export default function OsLivePage() {
  const pulse = useOSPulse(45000);
  const { lang } = useT();
  const es = lang === 'es';
  const taglines = AGENT_TAGLINE(es);

  return (
    <div className="bg-[#0B0D12] text-white min-h-screen" data-testid="os-live-page">
      <SEO
        title={es ? 'KITXENS OS — En Vivo' : 'KITXENS OS — Live'}
        description={es
          ? 'Mira al sistema operativo de IA de Kitxens trabajando en tiempo real: artículos, distribución social y agentes operando de forma autónoma.'
          : "Watch the Kitxens AI Operating System work in real time: articles, social distribution and agents operating autonomously."}
        path="/os"
      />
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pt-32">
        {/* Hero */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-emerald-300 text-[11px] tracking-[0.2em] uppercase font-bold px-3.5 py-1.5">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            {es ? 'Sistema operando ahora' : 'System operating now'}
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]">
            {es ? 'Esto no es una promesa. Está pasando ahora.' : "This isn't a promise. It's happening right now."}
          </h1>
          <p className="mt-6 text-neutral-400 text-lg leading-relaxed max-w-2xl">
            {es
              ? 'El AI Operating System de Kitxens crea, publica y distribuye conocimiento de forma autónoma — el mismo sistema que operamos para tu restaurante. Esta página muestra su actividad real.'
              : 'The Kitxens AI Operating System creates, publishes and distributes knowledge autonomously — the same system we operate for your restaurant. This page shows its real activity.'}
          </p>
        </div>

        {/* Live counters — zero counters step aside until they have real activity */}
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="os-live-counters">
          {[
            { v: pulse?.counts?.articles_published, l: es ? 'Artículos publicados' : 'Articles published' },
            { v: pulse?.counts?.social_packages, l: es ? 'Paquetes sociales generados' : 'Social packages generated' },
            { v: pulse?.counts?.posts_distributed, l: es ? 'Posts distribuidos' : 'Posts distributed' },
            { v: pulse?.counts?.os_events, l: es ? 'Eventos del sistema' : 'System events' },
            { v: pulse?.monitoring?.websites_analyzed, l: es ? 'Señales de restaurantes' : 'Restaurant signals' },
            { v: pulse?.monitoring?.ai_search_updates, l: es ? 'Actualizaciones de AI Search' : 'AI Search updates' },
          ]
            .filter((c) => c.v == null || c.v > 0)
            .slice(0, 4)
            .map((c) => <Counter key={c.l} value={c.v ?? '—'} label={c.l} />)}
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 items-start">
          {/* Event stream */}
          <div className="lg:col-span-7 rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-7" data-testid="os-live-stream">
            <div className="flex items-center justify-between">
              <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-neutral-500">
                <Activity className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? 'Flujo de eventos en vivo' : 'Live event stream'}
              </p>
              <span className="text-[10.5px] text-neutral-600">{es ? 'Datos reales del OS' : 'Real OS data'}</span>
            </div>
            <div className="mt-5 space-y-1">
              {(pulse?.events || []).slice(0, 10).map((e, i) => (
                <motion.div
                  key={`${e.type}-${e.created_at}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
                >
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F77F11] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] text-neutral-200 leading-snug">
                      {e.slug ? (
                        <Link to={`/magazine/${e.slug}`} className="hover:text-[#FFB672] transition-colors">{eventLabel(e, es)}</Link>
                      ) : eventLabel(e, es)}
                    </p>
                    <p className="text-[11px] text-neutral-600 mt-0.5">{timeAgo(e.created_at, es)} · {e.type}</p>
                  </div>
                </motion.div>
              ))}
              {!pulse?.events?.length && (
                <p className="text-[13px] text-neutral-500 py-6 text-center">{es ? 'Conectando con el sistema…' : 'Connecting to the system…'}</p>
              )}
            </div>
          </div>

          {/* Latest knowledge + how it flows */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-7" data-testid="os-live-latest">
              <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-neutral-500">
                <FileText className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? 'Último conocimiento publicado' : 'Latest published knowledge'}
              </p>
              <div className="mt-4 space-y-3">
                {(pulse?.latest_articles || []).map((a) => (
                  <Link
                    key={a.slug}
                    to={`/magazine/${a.slug}`}
                    className="group block rounded-xl bg-black/25 border border-white/10 hover:border-[#F77F11]/50 px-4 py-3.5 transition-colors"
                  >
                    <p className="text-[13.5px] font-semibold text-neutral-200 group-hover:text-white leading-snug">{a.title}</p>
                    <p className="text-[11px] text-neutral-600 mt-1">{a.category_slug} · {timeAgo(a.published_at, es)}</p>
                  </Link>
                ))}
              </div>
            </div>
            <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-7">
              <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-neutral-500">
                <Send className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? 'Cada artículo fluye solo' : 'Every article flows on its own'}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-neutral-300">
                {[es ? 'Conocimiento' : 'Knowledge', 'Web', 'RSS', 'LinkedIn · Facebook · X · Instagram · GBP', es ? 'Ventas' : 'Sales', es ? 'Métricas' : 'Metrics'].map((step, i, arr) => (
                  <React.Fragment key={step}>
                    <span className="rounded-md bg-white/[0.05] border border-white/12 px-2.5 py-1.5">{step}</span>
                    {i < arr.length - 1 && <ArrowRight className="w-3 h-3 text-neutral-600" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* The team, on shift */}
        <div className="mt-14" data-testid="os-live-team">
          <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-neutral-500">
            <Radio className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? 'El equipo, en turno 24/7' : 'The team, on shift 24/7'}
          </p>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {TEAM_AVATARS.map((m, i) => (
              <motion.div
                key={m.key}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#F77F11]/40 p-4 text-center transition-colors"
              >
                <span className="relative inline-block">
                  <span className="inline-flex w-14 h-14 rounded-full overflow-hidden ring-2 ring-white/10">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                  </span>
                  <span className="absolute bottom-0 right-0 flex w-3.5 h-3.5">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                    <span className="relative w-3.5 h-3.5 rounded-full bg-emerald-400 ring-2 ring-[#0B0D12]" />
                  </span>
                </span>
                <p className="mt-2.5 font-bold text-[13.5px]">{m.name}</p>
                <p className="text-[10.5px] text-neutral-500 leading-snug mt-0.5">{taglines[m.key] || m.role}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Live dashboard demo — what we build for clients */}
        <div className="mt-16 -mx-6 lg:-mx-10">
          <AutoDashboardShowcase />
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-3xl bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.015em] max-w-lg leading-tight">
              {es ? 'Este mismo sistema puede trabajar para tu restaurante.' : 'This same system can work for your restaurant.'}
            </h2>
            <p className="mt-2 text-white/85 text-[14.5px]">{es ? 'Desde $99/mes. Sin contratos forzosos.' : 'From $99/mo. No forced contracts.'}</p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <a
              href={`https://wa.me/14085901102?text=${encodeURIComponent(es ? 'Hola Kitxens, vi el OS en vivo y quiero esto para mi restaurante.' : "Hi Kitxens, I saw the OS live and I want this for my restaurant.")}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#F77F11] hover:bg-neutral-100 h-12 px-6 rounded-full text-sm font-semibold transition-colors"
              data-testid="os-live-cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" /> {es ? 'Lo quiero para mi restaurante' : 'I want this for my restaurant'}
            </a>
            <Link to="/operating-intelligence" className="inline-flex items-center gap-1 border border-white/50 hover:bg-white/10 text-white h-12 px-6 rounded-full text-sm font-semibold transition-colors" data-testid="os-live-cta-oi">
              {es ? 'Conocer la Inteligencia Operativa' : 'Discover the Operating Intelligence'} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      <ContinueExploring dark />
    </div>
  );
}
